/**
 * OracleIntroPanel.tsx - Contextual Introduction Panel
 *
 * Answers the user's first question: "What am I looking at?"
 * Displays purpose statement, user's query, key finding summary,
 * and cross-tab navigation buttons.
 *
 * Per wobbly-nibbling-chipmunk.md Phase 1
 * Per REASONING_ORACLE_PROTOTYPE_SELECTION_v3.md Section 12.3
 */

import { useMemo } from 'react'
import type { OracleResponse, BSCPerspective } from '@models/oracle'
import type { CenterTab } from '@contexts/TabNavigationContext'
import './OracleIntroPanel.css'

interface OracleIntroPanelProps {
  response: OracleResponse
  onNavigateToTab: (tab: CenterTab) => void
  onNavigateToWorkspaceSection?: (section: 'mece' | 'strategy-map') => void
  selectedHistoryIndex?: number
  totalHistoryCount?: number
  onViewLatest?: () => void
}

// Format BSC perspective for display
function formatPerspective(perspective: BSCPerspective | string | undefined): string {
  if (!perspective) return 'Unknown'
  const mapping: Record<string, string> = {
    Learning_Growth: 'Learning & Growth',
    Internal_Process: 'Internal Process',
    Customer: 'Customer',
    Financial: 'Financial',
  }
  return mapping[perspective] || perspective.replace(/_/g, ' ')
}

// Generate the "Key Finding" summary from inference data
function generateKeyFinding(response: OracleResponse): string {
  const chainCount = response.inference_chains.length
  const anchors = response.classification.anchor_concepts
  const perspectives = response.classification.bsc_perspectives

  if (chainCount === 0) {
    return 'No causal pathways were identified for this query.'
  }

  // Get unique anchor concepts
  const anchorNames = anchors.map((a) => a.concept).slice(0, 2)
  const anchor1 = anchorNames[0] || 'the query concept'
  const anchor2 = anchorNames[1] || anchor1

  // Get perspective span
  const persp1 = formatPerspective(perspectives[0])
  const persp2 = formatPerspective(perspectives[perspectives.length - 1])

  // Count matched nodes across all chains
  const totalMatched = response.inference_chains.reduce(
    (sum, chain) => sum + chain.matched_nodes.length,
    0
  )

  if (anchor1 === anchor2) {
    return (
      `Causeway traced ${chainCount} causal pathway${chainCount !== 1 ? 's' : ''} ` +
      `from ${anchor1.replace(/_/g, ' ')} (${persp1}) through the BSC causal model, ` +
      `validating ${totalMatched} business concepts.`
    )
  }

  return (
    `Causeway traced ${chainCount} causal pathway${chainCount !== 1 ? 's' : ''} ` +
    `connecting ${anchor1.replace(/_/g, ' ')} (${persp1}) to ${anchor2.replace(/_/g, ' ')} (${persp2}) ` +
    `through the BSC causal model, validating ${totalMatched} business concepts.`
  )
}

function OracleIntroPanel({
  response,
  onNavigateToTab,
  onNavigateToWorkspaceSection,
  selectedHistoryIndex = 0,
  totalHistoryCount = 1,
  onViewLatest,
}: OracleIntroPanelProps) {
  // Generate key finding summary
  const keyFinding = useMemo(() => generateKeyFinding(response), [response])

  return (
    <div className="oracle-intro-panel">
      {/* Key Finding — h2 + purpose statement removed (tab name + ⓘ tooltip on
          tier headers carry the same context without redundancy). Query echo +
          confidence are surfaced in the Summary card below. */}
      <div className="intro-key-finding">
        <span className="finding-label">Key Finding:</span>
        <span className="finding-text">{keyFinding}</span>
      </div>

      {/* Historical analysis indicator */}
      {selectedHistoryIndex > 0 && totalHistoryCount > 1 && (
        <div className="intro-history-indicator">
          <span>Viewing analysis {selectedHistoryIndex + 1} of {totalHistoryCount}</span>
          {onViewLatest && (
            <button className="view-latest-btn" onClick={onViewLatest}>
              View Latest
            </button>
          )}
        </div>
      )}

      {/* Cross-Tab Navigation */}
      <div className="intro-navigation">
        <button
          className="nav-button workspace"
          onClick={() => onNavigateToWorkspaceSection
            ? onNavigateToWorkspaceSection('mece')
            : onNavigateToTab('workspace')
          }
          title="View MECE pillars and tactics"
        >
          <span className="nav-icon">&#x1F4CB;</span>
          View MECE Pillars
          <span className="nav-arrow">&rarr;</span>
        </button>

        <button
          className="nav-button workspace"
          onClick={() => onNavigateToWorkspaceSection
            ? onNavigateToWorkspaceSection('strategy-map')
            : onNavigateToTab('workspace')
          }
          title="View Strategy Map objectives"
        >
          <span className="nav-icon">&#x1F5FA;</span>
          View Strategy Map
          <span className="nav-arrow">&rarr;</span>
        </button>

        <button
          className="nav-button report"
          onClick={() => onNavigateToTab('report')}
          title="View full Analysis Report"
        >
          <span className="nav-icon">&#x1F4C4;</span>
          View Analysis Report
          <span className="nav-arrow">&rarr;</span>
        </button>
      </div>
    </div>
  )
}

export default OracleIntroPanel
